import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme,
  Button,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Paper,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import SmsIcon from "@mui/icons-material/Sms";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ReceiveIcon from "@mui/icons-material/CallReceived";
import UpdateIcon from "@mui/icons-material/Update";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Update } from "@mui/icons-material";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:10000";
const TZ = "America/New_York";
const TIMEZONE_OFFSET = 4;

function getActivityLevel(hourStats, appointments, interactions) {
  let sum =
    (hourStats?.IG_Outbound || 0) +
    (hourStats?.IG_Inbound || 0) +
    (hourStats?.SMS_Inbound || 0);

  sum += appointments || 0;
  return sum;
}

function formatDateForDisplay(date) {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: TZ,
  });
}

function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: TZ,
  });
}

function formatHourRange(hour) {
  if (hour === null || hour === undefined) return "";

  const startHour = hour;
  const endHour = (hour + 1) % 24;

  const startStr = `${startHour % 12 === 0 ? 12 : startHour % 12
    }:00 ${startHour < 12 ? "AM" : "PM"}`;
  const endStr = `${endHour % 12 === 0 ? 12 : endHour % 12}:00 ${endHour < 12 ? "AM" : "PM"
    }`;

  return `${startStr} - ${endStr} ET`;
}

function getHourKeyFromVisualHour(visualHour, date) {
  const adjustedDate = new Date(date);
  const dataHour = (visualHour - TIMEZONE_OFFSET + 24) % 24;

  adjustedDate.setHours(dataHour + 8, 0, 0, 0);

  const year = adjustedDate.getFullYear();
  const month = String(adjustedDate.getMonth() + 1).padStart(2, "0");
  const day = String(adjustedDate.getDate()).padStart(2, "0");
  const hour = String(adjustedDate.getHours()).padStart(2, "0");

  return `${year}-${month}-${day} ${hour}:00`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        sx={{
          bgcolor: "rgba(32, 32, 32, 0.95)",
          color: "#fff",
          p: 1.5,
          border: "1px solid #333",
          boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          {label}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: "#98FF98" }}>
            {payload[0].value} Interactions
          </Typography>
        </Box>
      </Paper>
    );
  }

  return null;
};

const HourlyMetricsDisplay = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [metricsData, setMetricsData] = useState(null);
  const [selectedVisualHour, setSelectedVisualHour] = useState(
    new Date().getHours()
  );
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery("(max-width: 800px)");
  const isTablet = useMediaQuery("(max-width: 1100px)");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const chartRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const swipeContainerRef = useRef(null);

  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [dialogMessages, setDialogMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNextHour();
    } else if (isRightSwipe) {
      handlePreviousHour();
    }
  };

  const handlePreviousHour = () => {
    setSelectedVisualHour((prev) => (prev - 1 + 24) % 24);
  };

  const handleNextHour = () => {
    setSelectedVisualHour((prev) => (prev + 1) % 24);
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const metricsRes = await fetch(`${API_BASE}/metrics`);
        if (!metricsRes.ok) throw new Error("Failed to fetch metrics");
        const metrics = await metricsRes.json();

        setMetricsData(metrics);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  useEffect(() => {
    setSelectedVisualHour(new Date().getHours());
  }, [selectedDate]);

  const handlePreviousDay = () => {
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    setSelectedDate(prevDay);
  };

  const handleNextDay = () => {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);

    const today = new Date();
    if (nextDay <= today) {
      setSelectedDate(nextDay);
    }
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const fetchMessagesForTimeframe = async (
    startTime,
    endTime,
    messageType,
    direction
  ) => {
    setLoadingMessages(true);
    try {
      const params = new URLSearchParams({
        start_time: startTime,
        end_time: endTime,
      });

      if (messageType) {
        params.append("message_type", messageType);
      }

      if (direction) {
        params.append("direction", direction);
      }

      const response = await fetch(
        `${API_BASE}/notifications/timeframe?${params}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.statusText}`);
      }

      const data = await response.json();
      return data.notifications;
    } catch (error) {
      console.error("Error fetching messages:", error);
      return [];
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleOpenMessageDialog = async (type) => {
    const hourKey = getHourKeyFromVisualHour(selectedVisualHour, selectedDate);

    const etDate = new Date(selectedDate);
    etDate.setHours(selectedVisualHour, 0, 0, 0);

    const hourStart = new Date(etDate);
    const hourEnd = new Date(etDate);
    hourEnd.setHours(hourEnd.getHours() + 1);

    const startTimeISO = hourStart.toISOString();
    const endTimeISO = hourEnd.toISOString();

    let messageType, direction, dialogTitle;

    if (type === "IG_Outbound") {
      messageType = "IG";
      direction = "outbound";
      dialogTitle = "Instagram Follow-Ups";
    } else if (type === "IG_Inbound") {
      messageType = "IG";
      direction = "inbound";
      dialogTitle = "Instagram Replies";
    } else if (type === "SMS_Outbound") {
      messageType = "SMS";
      direction = "outbound";
      dialogTitle = "SMS Follow-Ups";
    } else if (type === "SMS_Inbound") {
      messageType = "SMS";
      direction = "inbound";
      dialogTitle = "SMS Replies";
    }

    setDialogType(dialogTitle);
    setMessageDialogOpen(true);

    const messages = await fetchMessagesForTimeframe(
      startTimeISO,
      endTimeISO,
      messageType,
      direction
    );

    setDialogMessages(messages);
  };

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setDialogMessages([]);
  };

  if (loading)
    return (
      <Box
        sx={{
          color: "#98FF98",
          textAlign: "center",
          marginTop: 16,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="inherit" size={48} />
      </Box>
    );

  if (error)
    return (
      <div style={{ color: "red", textAlign: "center", marginTop: 80 }}>
        {error}
      </div>
    );

  const hourlyStats = metricsData.hourly_stats || {};
  const appointments = metricsData.appointments || [];

  const bookingsByHour = {};
  for (const appt of appointments) {
    if (!appt.start_time) continue;
    const dt = new Date(appt.start_time + " ET");
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    const hour = String(dt.getHours()).padStart(2, "0");
    const hourKey = `${year}-${month}-${day} ${hour}:00`;
    bookingsByHour[hourKey] = (bookingsByHour[hourKey] || 0) + 1;
  }

  const chartData = [];
  for (let visualHour = 0; visualHour < 24; visualHour++) {
    const hourDisplay = visualHour;

    const hourKey = getHourKeyFromVisualHour(visualHour, selectedDate);

    const stats = hourlyStats[hourKey] || {};
    const bookings = bookingsByHour[hourKey] || 0;
    const interactions = 0;
    const activity = getActivityLevel(stats, bookings, interactions);

    const formattedHour = `${visualHour % 12 === 0 ? 12 : visualHour % 12
      } ${visualHour < 12 ? "AM" : "PM"}`;

    chartData.push({
      hourKey,
      visualHour,
      formattedHour,
      activity: activity === 0 ? 0.5 : activity,
      actualActivity: activity,
      igOutbound: stats.IG_Outbound || 0,
      igInbound: stats.IG_Inbound || 0,
      smsOutbound: stats.SMS_Outbound || 0,
      smsInbound: stats.SMS_Inbound || 0,
      bookings,
    });
  }

  const handleChartClick = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const clickedData = data.activePayload[0].payload;
      setSelectedVisualHour(clickedData.visualHour);
    }
  };

  const dataHourKey = getHourKeyFromVisualHour(selectedVisualHour, selectedDate);

  const selectedStats = hourlyStats[dataHourKey] || {};
  const selectedBookings = bookingsByHour[dataHourKey] || 0;

  const selectedHourRange = formatHourRange(selectedVisualHour);

  const isToday = new Date().toDateString() === selectedDate.toDateString();

  const MobileMetric = ({ icon, label, value, color, onClick }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        bgcolor: "#2a2a2a",
        borderRadius: 2,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        "&:hover": onClick
          ? {
            transform: "scale(1.02)",
            bgcolor: "#333",
          }
          : {},
      }}
      onClick={onClick}
    >
      <Avatar
        sx={{
          bgcolor: color,
          width: isMobile ? 40 : 48,
          height: isMobile ? 40 : 48,
          mr: 2,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight="bold"
          sx={{ color }}
        >
          {value}
        </Typography>
        <Typography
          variant={isMobile ? "body2" : "body1"}
          sx={{ color: "#bbb" }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );

  const DesktopMetric = ({ icon, label, value, color, onClick }) => (
    <Stack
      alignItems="center"
      spacing={1}
      sx={{
        my: 2,
        pt: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s",
        "&:hover": onClick
          ? {
            transform: "scale(1.05)",
          }
          : {},
      }}
      onClick={onClick}
    >
      <Avatar
        sx={{
          bgcolor: color,
          width: 56,
          height: 56,
          mb: 1,
          boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
        }}
      >
        {icon}
      </Avatar>
      <Typography variant="h3" fontWeight="bold" sx={{ color }}>
        {value}
      </Typography>
      <Typography variant="h6" sx={{ color: "#bbb" }}>
        {label}
      </Typography>
    </Stack>
  );

  const CustomTooltipUpdated = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper
          sx={{
            bgcolor: "rgba(32, 32, 32, 0.95)",
            color: "#fff",
            p: 1.5,
            border: "1px solid #333",
            boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {label}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: "#98FF98" }}>
              {data.actualActivity} Interactions
            </Typography>
          </Box>
        </Paper>
      );
    }

    return null;
  };

  return (
    <Fade in>
      <Box
        sx={{
          bgcolor: "#181818",
          px: isTablet ? 2 : 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 2,
            mb: isTablet ? 2 : 4,
            bgcolor: "#202020",
            borderRadius: "16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              my: 1,
              gap: 2,
              mr: isMobile ? 2 : 0,
            }}
          >
            <IconButton
              size={isMobile ? "small" : "medium"}
              onClick={handlePreviousDay}
              sx={{
                width: isMobile ? "35px" : "40px",
                color: "#98FF98",
              }}
            >
              <ArrowBackIosIcon sx={{ mr: "-6px" }} />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CalendarTodayIcon sx={{ color: "#98FF98" }} />
              <Typography
                variant="h6"
                sx={{
                  color: "#fff",
                  minWidth: isMobile ? 135 : 125,
                  textAlign: "center",
                }}
              >
                {formatDateForDisplay(selectedDate)}
              </Typography>
            </Box>

            <IconButton
              size={isMobile ? "small" : "medium"}
              onClick={handleNextDay}
              sx={{
                width: isMobile ? "35px" : "40px",
                color: "#98FF98",
                borderColor: "#98FF98",
                "&:hover": {
                  borderColor: "#7aff7a",
                  backgroundColor: "rgba(152, 255, 152, 0.2)",
                },
                mx: isMobile ? -2 : 0,
              }}
              disabled={isToday}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>

          
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mb: 1,
            }}
          >
            <IconButton
              onClick={handlePreviousHour}
              sx={{
                color: "#98FF98",
                bgcolor: "rgba(152, 255, 152, 0.1)",
                "&:hover": {
                  bgcolor: "rgba(152, 255, 152, 0.2)",
                },
                width: isMobile ? 32 : 36,
                height: isMobile ? 32 : 36,
              }}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: isMobile ? 140 : 160,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "#98FF98",
                  letterSpacing: 1,
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {selectedHourRange}
              </Typography>
              {isMobile && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    textAlign: "center",
                    fontSize: "0.7rem",
                  }}
                >
                  Swipe chart to navigate
                </Typography>
              )}
            </Box>

            <IconButton
              onClick={handleNextHour}
              sx={{
                color: "#98FF98",
                bgcolor: "rgba(152, 255, 152, 0.1)",
                "&:hover": {
                  bgcolor: "rgba(152, 255, 152, 0.2)",
                },
                width: isMobile ? 32 : 36,
                height: isMobile ? 32 : 36,
              }}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          </Box>

          <IconButton
            size={isMobile ? "small" : "medium"}
            onClick={handleToday}
            disabled={isToday}
            sx={{
              mt: 1,
              mb: -1,
              backgroundColor: "#282828",
              color: "#98FF98",
              borderColor: "#98FF98",
              "&:hover": {
                borderColor: "#7aff7a",
                backgroundColor: "rgba(152, 255, 152, 0.2)",
              },
            }}
          >
            <UpdateIcon />
          </IconButton>

          
          <Box
            ref={swipeContainerRef}
            sx={{
              width: "100%",
              height: isMobile ? 200 : 240,
              borderRadius: 2,
              p: isMobile ? 1 : 2,
              touchAction: "pan-y",
            }}
            onTouchStart={isMobile ? onTouchStart : undefined}
            onTouchMove={isMobile ? onTouchMove : undefined}
            onTouchEnd={isMobile ? onTouchEnd : undefined}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 10,
                  right: isMobile ? 10 : 20,
                  left: 5,
                  bottom: 10,
                }}
                onClick={handleChartClick}
                onMouseMove={(state) => {
                  if (state && state.activeTooltipIndex !== undefined) {
                    setHoveredIndex(state.activeTooltipIndex);
                  }
                }}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="rgba(255,255,255,0.1)"
                />
                <XAxis
                  dataKey="formattedHour"
                  tick={{ fill: "#aaa", fontSize: isMobile ? 10 : 12 }}
                  tickLine={false}
                  axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                  interval={isTablet ? 2 : 0}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fill: "#aaa", fontSize: isMobile ? 10 : 12 }}
                  tickLine={false}
                  axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                  width={30}
                />
                <Tooltip
                  content={<CustomTooltipUpdated />}
                  cursor={{ fill: "rgba(255, 255, 255, 0.05)" }}
                />
                <Bar dataKey="activity" radius={[2, 2, 0, 0]}>
                  {chartData.map((entry, index) => {
                    const isSelected = index === selectedVisualHour;
                    const isHovered = index === hoveredIndex;

                    let fill = "#666";
                    if (isSelected) {
                      fill = "#98FF98";
                    } else if (isHovered) {
                      fill = "rgba(152, 255, 152, 0.6)";
                    }

                    return <Cell key={`cell-${index}`} fill={fill} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {isMobile ? (
          <Box sx={{ width: "100%", pb: 6 }}>
            <Stack spacing={2}>
              <Card
                sx={{
                  borderRadius: 4,
                  background: "#202020",
                  color: "#fff",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: "#98FF98", mb: 2, textAlign: "center" }}
                  >
                    Instagram
                  </Typography>
                  <Stack spacing={2}>
                    <MobileMetric
                      icon={<InstagramIcon />}
                      label="Follow-Ups"
                      value={selectedStats.IG_Outbound || 0}
                      color="#F56040"
                      onClick={() => handleOpenMessageDialog("IG_Outbound")}
                    />
                    <MobileMetric
                      icon={<InstagramIcon />}
                      label="Replies"
                      value={selectedStats.IG_Inbound || 0}
                      color="#833AB4"
                      onClick={() => handleOpenMessageDialog("IG_Inbound")}
                    />
                  </Stack>
                </CardContent>
              </Card>

              <Card
                sx={{
                  borderRadius: 4,
                  background: "#202020",
                  color: "#fff",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: "#98FF98", mb: 2, textAlign: "center" }}
                  >
                    SMS
                  </Typography>
                  <Stack spacing={2}>
                    <MobileMetric
                      icon={<SmsIcon />}
                      label="Follow-Ups"
                      value={selectedStats.SMS_Outbound || 0}
                      color="#25D366"
                      onClick={() => handleOpenMessageDialog("SMS_Outbound")}
                    />
                    <MobileMetric
                      icon={<SmsIcon />}
                      label="Replies"
                      value={selectedStats.SMS_Inbound || 0}
                      color="#128C7E"
                      onClick={() => handleOpenMessageDialog("SMS_Inbound")}
                    />
                  </Stack>
                </CardContent>
              </Card>

              <Card
                sx={{
                  borderRadius: 4,
                  background: "#202020",
                  color: "#fff",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: "#98FF98", mb: 2, textAlign: "center" }}
                  >
                    Conversions
                  </Typography>
                  <Stack spacing={2}>
                    <MobileMetric
                      icon={<TouchAppIcon />}
                      label="New Leads"
                      value={0}
                      color="#0099FF"
                    />
                    <MobileMetric
                      icon={<EventAvailableIcon />}
                      label="Booked"
                      value={selectedBookings}
                      color="#98FF98"
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Grid2 container spacing={2}>
              <Box
                sx={{
                  width: "100%",
                  display: isMobile ? "block" : "flex",
                  justifyContent: "space-between",
                }}
              >
                <Grid2
                  item
                  sx={{
                    width: isMobile ? "100%" : "32%",
                    mb: isMobile ? 4.5 : 0,
                  }}
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      background: "#202020",
                      color: "#fff",
                      minWidth: isTablet ? 200 : 260,
                      minHeight: 180,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <CardContent>
                      <DesktopMetric
                        icon={<InstagramIcon fontSize="large" />}
                        label="IG Follow-Ups"
                        value={selectedStats.IG_Outbound || 0}
                        color="#F56040"
                        onClick={() => handleOpenMessageDialog("IG_Outbound")}
                      />
                      <DesktopMetric
                        icon={<InstagramIcon fontSize="large" />}
                        label="IG Replies"
                        value={selectedStats.IG_Inbound || 0}
                        color="#833AB4"
                        onClick={() => handleOpenMessageDialog("IG_Inbound")}
                      />
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2
                  item
                  sx={{
                    width: isMobile ? "100%" : "32%",
                    mb: isMobile ? 4.5 : 0,
                    pl: isTablet ? 0 : 1,
                  }}
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      background: "#202020",
                      color: "#fff",
                      minWidth: isTablet ? 200 : 260,
                      minHeight: 180,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <CardContent>
                      <DesktopMetric
                        icon={<SmsIcon fontSize="large" />}
                        label="SMS Follow-Ups"
                        value={selectedStats.SMS_Outbound || 0}
                        color="#25D366"
                        onClick={() => handleOpenMessageDialog("SMS_Outbound")}
                      />
                      <DesktopMetric
                        icon={<SmsIcon fontSize="large" />}
                        label="SMS Replies"
                        value={selectedStats.SMS_Inbound || 0}
                        color="#128C7E"
                        onClick={() => handleOpenMessageDialog("SMS_Inbound")}
                      />
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2
                  item
                  sx={{
                    width: isMobile ? "100%" : "32%",
                    mb: isMobile ? 4.5 : 0,
                    pl: isTablet ? 0 : 1,
                  }}
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      background: "#202020",
                      color: "#fff",
                      minWidth: isTablet ? 200 : 260,
                      minHeight: 180,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <CardContent>
                      <DesktopMetric
                        icon={<TouchAppIcon fontSize="large" />}
                        label="New Leads"
                        value={0}
                        color="#0099FF"
                      />
                      <DesktopMetric
                        icon={<EventAvailableIcon fontSize="large" />}
                        label="Booked"
                        value={selectedBookings}
                        color="#98FF98"
                      />
                    </CardContent>
                  </Card>
                </Grid2>
              </Box>
            </Grid2>
          </Box>
        )}

        <Dialog
          fullScreen={fullScreen}
          open={messageDialogOpen}
          onClose={handleCloseMessageDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "#202020",
              color: "#fff",
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#181818",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ color: "#98FF98" }}>
              {dialogType} ({dialogMessages.length})
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseMessageDialog}
              aria-label="close"
            >
              <CloseIcon sx={{ color: "#98FF98" }} />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: "#202020", p: 0 }}>
            {loadingMessages ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 4,
                }}
              >
                <CircularProgress sx={{ color: "#98FF98" }} />
              </Box>
            ) : dialogMessages.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body1" sx={{ color: "#bbb" }}>
                  No messages found for this time period.
                </Typography>
              </Box>
            ) : (
              <List sx={{ width: "100%", bgcolor: "#202020", p: 0 }}>
                {dialogMessages.map((message, index) => (
                  <React.Fragment key={message.id || index}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        py: 2,
                        px: 3,
                        bgcolor:
                          message.direction === "outbound"
                            ? "rgba(152, 255, 152, 0.05)"
                            : "transparent",
                      }}
                    >
                      <Box sx={{ display: "flex", width: "100%" }}>
                        <Avatar
                          sx={{
                            bgcolor:
                              message.direction === "outbound"
                                ? "#25D366"
                                : "#833AB4",
                            mr: 2,
                          }}
                        >
                          {message.direction === "outbound" ? (
                            <SendIcon />
                          ) : (
                            <ReceiveIcon />
                          )}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Chip
                                label={
                                  message.direction === "outbound"
                                    ? "Sent"
                                    : "Received"
                                }
                                size="small"
                                sx={{
                                  bgcolor:
                                    message.direction === "outbound"
                                      ? "#25D366"
                                      : "#833AB4",
                                  color: "#fff",
                                }}
                              />
                              <Chip
                                label={message.messageType}
                                size="small"
                                sx={{
                                  bgcolor:
                                    message.messageType === "IG"
                                      ? "#F56040"
                                      : "#128C7E",
                                  color: "#fff",
                                }}
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#bbb" }}
                            >
                              {formatTime(message.timestamp)}
                            </Typography>
                          </Box>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor:
                                message.direction === "outbound"
                                  ? "#2a2a2a"
                                  : "#333",
                              borderRadius: 2,
                              wordBreak: "break-word",
                            }}
                          >
                            <Typography variant="body1">
                              {message.body}
                            </Typography>
                          </Paper>
                          {message.contactId && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#bbb",
                                mt: 1,
                                display: "block",
                              }}
                            >
                              Contact ID:
                              <Button
                                component="a"
                                href={`https://app.gohighlevel.com/v2/location/ZghV6U2wnAogcW6SRaAr/contacts/detail/${message.contactId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: "#98FF98",
                                  textTransform: "none",
                                  padding: "0 4px",
                                  minWidth: "auto",
                                  fontSize: "inherit",
                                  fontWeight: "normal",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    textDecoration: "underline",
                                  },
                                }}
                              >
                                {message.contactId}
                              </Button>
                            </Typography>
                          )}
                          {message.status && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#bbb", display: "block" }}
                            >
                              Status: {message.status}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </ListItem>
                    {index < dialogMessages.length - 1 && (
                      <Divider sx={{ bgcolor: "#333" }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ bgcolor: "#181818" }}>
            <Button
              onClick={handleCloseMessageDialog}
              sx={{
                color: "#98FF98",
                "&:hover": {
                  backgroundColor: "rgba(152, 255, 152, 0.1)",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default HourlyMetricsDisplay;
